import { useEffect } from "react";
import { colors } from "../theme";

export type ProcessingPhase =
  | "idle"
  | "connecting"
  | "signing"
  | "verifying"
  | "success";

interface ProcessingOverlayProps {
  phase: ProcessingPhase;
  // Attempt / total for the verifying phase so the user sees that
  // a long wait is expected (TonCenter indexing takes ~30s).
  attempt?: number;
  totalAttempts?: number;
  subtitle?: string;
}

const PHASE_COPY: Record<Exclude<ProcessingPhase, "idle">, { title: string; sub: string }> = {
  connecting: {
    title: "Connecting wallet…",
    sub: "Finish in Tonkeeper, then come back to this screen.",
  },
  signing: {
    title: "Confirm in Tonkeeper",
    sub: "Sign the transaction in your wallet app. Don't close this screen.",
  },
  verifying: {
    title: "Verifying deposit…",
    sub: "We're waiting for the TON network to confirm your payment. This takes ~30s.",
  },
  success: {
    title: "Success!",
    sub: "Opening the bet…",
  },
};

export default function ProcessingOverlay({
  phase,
  attempt,
  totalAttempts,
  subtitle,
}: ProcessingOverlayProps) {
  // While a phase is active, swallow the hardware/Telegram back button so
  // the user can't yank themselves out of a half-completed on-chain tx.
  useEffect(() => {
    if (phase === "idle") return;
    const tg = window.Telegram?.WebApp;
    const onBack = () => {
      // No-op — we intentionally block back navigation while the deposit is
      // in flight. Once the phase returns to idle (error or success) this
      // handler is removed and normal navigation resumes.
    };
    try {
      tg?.BackButton?.onClick?.(onBack);
    } catch {}
    const handler = (e: PopStateEvent) => {
      e.preventDefault();
      window.history.pushState(null, "", window.location.href);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handler);
    return () => {
      window.removeEventListener("popstate", handler);
      try {
        tg?.BackButton?.offClick?.(onBack);
      } catch {}
    };
  }, [phase]);

  if (phase === "idle") return null;

  const copy = PHASE_COPY[phase];
  const showProgress =
    phase === "verifying" && typeof attempt === "number" && typeof totalAttempts === "number";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(6, 6, 11, 0.92)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 32,
        zIndex: 2000,
        textAlign: "center",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Spinner accent={phase === "success" ? colors.success : colors.primary} />
      <div
        style={{
          fontSize: 20,
          fontWeight: 800,
          color: colors.white,
          marginTop: 24,
          marginBottom: 8,
        }}
      >
        {copy.title}
      </div>
      <div style={{ fontSize: 14, color: colors.grayLight, maxWidth: 320, lineHeight: 1.5 }}>
        {subtitle ?? copy.sub}
      </div>
      {showProgress && (
        <div
          style={{
            marginTop: 20,
            fontSize: 13,
            color: colors.grayLight,
            letterSpacing: 0.5,
          }}
        >
          Attempt {attempt} / {totalAttempts}
        </div>
      )}
    </div>
  );
}

function Spinner({ accent }: { accent: string }) {
  return (
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        border: `3px solid ${colors.borderLight}`,
        borderTopColor: accent,
        animation: "sidebets-spin 900ms linear infinite",
      }}
    >
      <style>{`@keyframes sidebets-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
